import Masker from 'hooks/useMasker'
import moment from 'moment'
import countryCodes from 'utils/countryCodes'

const addComma = (num: string | number) => num?.toString().replace(/([0-9]+)([0-9]{2})$/g, "$1,$2")
const removeNonNumeric = (num: string | number) => num?.toString().replace(/[^0-9]/g, "")
const addPoints = (num: string | number) => num?.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')

const horasCSNToFloat = (num: string | number): number => {
  if (num) {
    if (typeof num === 'string') {
      if (num?.toString()?.length === 2 || num?.toString()?.length === 1) {
        return parseFloat(num.toString().replace(/[^0-9]/g, ''))
      }

      if (num?.toString()?.length >= 3) {
        return parseFloat(num.toString().replace(/[^0-9]/g, '').replace(/(.*)([0-9]{2})/g, '$1.$2'))
      }
    }

    if (typeof num === 'number') {
      return num
    }
  }

  return 0
}

const horasCSN = (num: string | number): string => {
  if (!num) {
    return null
  }

  if (typeof num === 'number') {
    let [hours, minutes] = num?.toString().split('.')

    if (!hours) {
      hours = '0'
    }

    if (!minutes) {
      minutes = '00'
    }

    if (minutes?.length === 1) {
      minutes = `${minutes}0`
    }

    return addPoints(addComma(removeNonNumeric(`${hours}${minutes}`)))
  }

  return addPoints(addComma(removeNonNumeric(num)))
}

export default {
  horasCSN,
  horasCSNToFloat,
  alfanumeric: /[^\w\s]/gi,
  capitalizeString: (value: string) =>
    value.replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase())),
  cep: (string) =>
    typeof string === 'string'
      ? string.slice(0, 5) + '-' + string.slice(5)
      : string,
  competencia: (competencia: string | number): string => {
    if (!competencia) return ''

    competencia = String(competencia)

    if (competencia?.includes('/')) {
      const [month, year] = competencia
        .split('/')
        ?.sort((a, b) => a.length - b.length)

      return `${month}/${year}`
    }

    const monthPaterrn = /^0[0-9]|^1[012]|0[0-9]$|1[012]$/

    const year = competencia.replace(monthPaterrn, '')
    const month = competencia?.match(monthPaterrn)

    if ([month, year].includes(null)) {
      return ''
    }

    return `${month[0]}/${year}`
  },
  contato: (value: any) => {
    try {
      // TELEFONE
      let cleanValue = value.replace(/[^\d]/g, '')
      if (cleanValue.length < 11) {
        cleanValue = cleanValue.replace(/\D/g, '')
        cleanValue = cleanValue.replace(/^(\d{1})/, '($1')
        cleanValue = cleanValue.replace(/^[(](\d{2})(\d{1})/, '($1) $2')
        cleanValue = cleanValue.replace(
          /^[(](\d{2})[)][ ](\d{4})(\d{1})/,
          '($1) $2-$3'
        )
        return cleanValue
      }
      //CELULAR
      cleanValue = cleanValue.replace(/\D/g, '')
      cleanValue = cleanValue.replace(/^(\d{1})/, '($1')
      cleanValue = cleanValue.replace(/^[(](\d{2})(\d{1})/, '($1) $2')
      cleanValue = cleanValue.replace(
        /^[(](\d{2})[)][ ](\d{5})(\d{1})/,
        '($1) $2-$3'
      )
      return cleanValue
    } catch (err) {
      return value
    }
  },
  contatoInternacional: (telNumber: string): string => {
    if (!telNumber) return ''

    let telMask = ['']

      ;[4, 3, 2, 1]?.forEach((codeLength) => {
        const code = countryCodes.find(
          (cdCountry) => cdCountry === telNumber.slice(0, codeLength)
        )

        if (code) {
          const countryCodeMask = Array.from({ length: code.length })
            .map((_) => '#')
            .join('')
          telMask = [
            `+${countryCodeMask} (##) ####-####`,
            `+${countryCodeMask} (##) #####-####`
          ]
        }
      })

    return Masker(telNumber, telMask)
  },

  currency: (value, style: 'currency' | 'decimal' | 'percent' = 'percent') => {
    if (style === 'decimal') {
      return new Intl.NumberFormat('pt-BR', { maximumSignificantDigits: 10 }).format(value)
    }

    if (style === 'percent') {
      return new Intl.NumberFormat('pt-BR', {
        currency: 'BRL',
        minimumFractionDigits: 2,
        style: 'currency'
      }).format(value)
    }

    return new Intl.NumberFormat('pt-BR', {
      currency: 'BRL',
      minimumFractionDigits: 2,
      style: 'currency'
    }).format(value)
  },
  dateIfExists: (obj: Object, format: DateFormats = 'YYYY-MM-DD'): Object => {
    const FORMATS = {
      'YYYY-MM-DD': (date: any) => moment(date).format('YYYY-MM-DD'),
      moment: (date: any) => moment(date)
    }
    let result: Object = {}
    Object.keys(obj).map((key) => {
      if (obj[key]) {
        result = { ...result, [key]: FORMATS[format](obj[key]) }
      }
    })
    return result
  },
  dayToDate: (dia: string) =>
    moment(`${moment().year()}-0${moment().month() + 1}-${dia}`).toDate(),

  documento: (value: string) => {
    if (!value) return '- - - - '
    //CPF
    let cleanValue = value
      ?.replace(/[^A-Za-z0-9]/g, '')
      ?.toUpperCase()
      ?.trim()

    if (/^[A-Z-a-z]{2}/g.test(cleanValue)) {
      return cleanValue.toUpperCase()
    }

    if (cleanValue.length === 11 && !isNaN(parseInt(value))) {
      cleanValue = cleanValue.replace(/\D/g, '')
      cleanValue = cleanValue.replace(/^(\d{3})(\d{1})/, '$1.$2')
      cleanValue = cleanValue.replace(/^(\d{3}).(\d{3})(\d{1})/, '$1.$2.$3')
      cleanValue = cleanValue.replace(
        /^(\d{3}).(\d{3}).(\d{3})(\d)/,
        '$1.$2.$3-$4'
      )
      return cleanValue
    }
    //CNPJ
    if (cleanValue.length === 14 && !isNaN(parseInt(value))) {
      cleanValue = cleanValue.replace(/\D/g, '')
      cleanValue = cleanValue.replace(/^(\d{2})(\d{1})/, '$1.$2')
      cleanValue = cleanValue.replace(/^(\d{2}).(\d{3})(\d{1})/, '$1.$2.$3')
      cleanValue = cleanValue.replace(
        /^(\d{2}).(\d{3}).(\d{3})(\d{1})/,
        '$1.$2.$3/$4'
      )
      cleanValue = cleanValue.replace(
        /^(\d{2}).(\d{3}).(\d{3})[/](\d{4})(\d)/,
        '$1.$2.$3/$4-$5'
      )
      return cleanValue
    }

    return cleanValue
  },
  //file: (value: string) => value.replace(/data:.*\/.*;base64,/g, ""),
  file: (value: string) => value.replace(/data:.*;base64,/g, ''),
  formatCdDocto: (value: string | number): string => {
    const specialCharacters = /[^a-zA-Z0-9\s]/g

    const passaportPattern = /^[A-Z]{2}[0-9]+$/g
    const cnpjPattern = /[0-9]{14}/g
    const cpfPattern = /[0-9]{11}/g

    value = String(value)
    value = value.replace(specialCharacters, '')

    if (isNaN(parseInt(value))) {
      return Masker(value, ['NN##########'])
    }

    if (passaportPattern.test(value)) {
      return Masker(value, ['NN##########'])
    }

    if (cnpjPattern.test(value)) {
      return Masker(value, ['##.###.###/####-##'])
    }

    if (cpfPattern.test(value)) {
      return Masker(value, ['###.###.###-##'])
    }

    return '- - - - -'
  },
  formatDD: (value: any) => moment(value).format('DD'),
  formatDDMMYYYY: (value: any): string => {
    if (!value) return '------'

    return moment(value).format('YYYY') === '0001'
      ? '------'
      : moment(value).format('L')
  },
  formatMMYYYYWithValueMask: (value: any) => moment(value, 'DD/MM/YYYY').format('DD/MM/YYYY'),
  formatMMYYYY: (value: any) => moment(value).format('MM/YYYY'),
  formatYYYYMM: (value: any) => moment(value).format('YYYYMM'),
  formatYYYYMMDD: (value: any) => moment(value).format('YYYYMMDD'),
  formatYYYYMMDD2: (value: any) => moment(value).format('YYYY-MM-DD'),
  numeric: /\D/g,
  orderByLast: (array: Array<any>) => [...array].reverse(),
  orderByPropText: (array: Array<any>, name: string) =>
    array?.sort((a, b) => a[name].localeCompare(b[name])),
  orderByPropValue: (array: Array<any>, value: string) =>
    array?.sort((a, b) => a[value] - b[value]),
  percentage: (partialValue: number, totalValue: number): number =>
    (100 * partialValue) / totalValue,
  processo: (value: string) => {
    if (!value) return '- - - - '
    return value
      .replace(/[^\d]/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{8})(\d{1,2})/, '$1-$2')
  },
  propIfExists: (obj: Object, formatFunction?: Function): Object => {
    let result: Object = {}

    if (obj === null || obj === undefined) return result
    else {
      Object.keys(obj).map((key) => {
        // Não trás a prop se for null/undefined
        if (obj[key] === undefined || obj[key] === null) return
        // Traz a prop se for boolean, string com pelo menos um caracter ou array não vazia
        if (obj[key] === true || obj[key] === false || obj[key].length > 0) {
          result = {
            ...result,
            [key]: formatFunction ? formatFunction(obj[key]) : obj[key]
          }
        }
      })
      return result
    }
  },

  strToBoolOrNull: (value: string) => {
    return value === 'true' ? true : value === 'false' ? false : null
  },
  // value?.replace(/[^\d]/g, ""), ANTIGO
  toFloat: (value: any) => parseFloat(value),
  unMask: (value: any) => value?.replace(/[^A-Za-z0-9]/g, '')
}

type DateFormats = 'YYYY-MM-DD' | 'moment'
